import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Instagram, Youtube, Twitter, Mail, Users, Facebook, Globe, Podcast, Video, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SocialModal from '../components/SocialModal';
import './InfluencerProfile.css';

const InfluencerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socialModal, setSocialModal] = useState({ open: false, platform: '', handle: '' });

  useEffect(() => {
    fetchInfluencer();
  }, [id]);

  const fetchInfluencer = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/influencers/public/${id}`);
      setInfluencer(response.data);
    } catch (error) {
      console.error('Error fetching influencer:', error);
      toast.error('Failed to load influencer profile');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialClick = (platform, handle) => {
    setSocialModal({ open: true, platform, handle });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loader"></div>
      </div>
    );
  }

  if (!influencer) return null;

  return (
    <div className="profile-container">
      <div className="profile-header">
        <Button 
          onClick={() => navigate('/')} 
          variant="ghost" 
          className="back-btn"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Back to Home
        </Button>
      </div>

      <div className="profile-content">
        <div className="profile-hero">
          <div className="profile-image-container" data-testid="profile-image">
            {influencer.profile_image_url ? (
              <img src={influencer.profile_image_url} alt={influencer.name} />
            ) : (
              <div className="placeholder-image">
                <Users size={64} />
              </div>
            )}
          </div>
          
          <div className="profile-info">
            <h1 className="profile-name" data-testid="profile-name">{influencer.name}</h1>
            
            <div className="profile-categories">
              {influencer.categories.map((cat, idx) => (
                <span key={idx} className="category-tag" data-testid={`category-${cat}`}>{cat}</span>
              ))}
            </div>
            
            <div className="profile-status">
              <span className={`availability ${influencer.availability ? 'available' : 'unavailable'}`} data-testid="availability-status">
                {influencer.availability ? '● Available for collaborations' : '● Currently unavailable'}
              </span>
              {influencer.estimated_charges && (
                <span className="charges-badge" data-testid="estimated-charges">
                  <DollarSign size={16} />
                  {influencer.estimated_charges}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="profile-details">
          {/* Video Section */}
          {influencer.video_url && (
            <section className="detail-section video-section">
              <h2 className="section-title">
                <Video size={24} />
                Featured Video
              </h2>
              <div className="video-container">
                <a 
                  href={influencer.video_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="video-link"
                  data-testid="video-link"
                >
                  <Video size={48} />
                  <span>Watch Video</span>
                </a>
              </div>
            </section>
          )}

          <section className="detail-section">
            <h2 className="section-title">About</h2>
            <p className="bio-text" data-testid="bio-text">{influencer.bio}</p>
          </section>

          {/* Capabilities Section */}
          {influencer.capabilities && influencer.capabilities.length > 0 && (
            <section className="detail-section">
              <h2 className="section-title">
                <CheckCircle size={24} />
                Capabilities & Services
              </h2>
              <ul className="capabilities-list">
                {influencer.capabilities.map((capability, idx) => (
                  <li key={idx} data-testid={`capability-${idx}`}>
                    <CheckCircle size={18} className="check-icon" />
                    {capability}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Availability Calendar */}
          {influencer.availability_calendar && influencer.availability_calendar.length > 0 && (
            <section className="detail-section">
              <h2 className="section-title">
                <Calendar size={24} />
                Available Dates
              </h2>
              <div className="calendar-dates">
                {influencer.availability_calendar.map((date, idx) => (
                  <span key={idx} className="date-badge" data-testid={`available-date-${idx}`}>
                    {formatDate(date)}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="detail-section">
            <h2 className="section-title">Social Media</h2>
            <div className="social-links">
              {influencer.social_handles.instagram && (
                <button
                  onClick={() => handleSocialClick('instagram', influencer.social_handles.instagram)}
                  className="social-link"
                  data-testid="instagram-link"
                >
                  <Instagram />
                  <div>
                    <div className="social-label">Instagram</div>
                    <div className="social-handle">@{influencer.social_handles.instagram}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.youtube && (
                <button
                  onClick={() => handleSocialClick('youtube', influencer.social_handles.youtube)}
                  className="social-link"
                  data-testid="youtube-link"
                >
                  <Youtube />
                  <div>
                    <div className="social-label">YouTube</div>
                    <div className="social-handle">@{influencer.social_handles.youtube}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.tiktok && (
                <button
                  onClick={() => handleSocialClick('tiktok', influencer.social_handles.tiktok)}
                  className="social-link"
                  data-testid="tiktok-link"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  <div>
                    <div className="social-label">TikTok</div>
                    <div className="social-handle">@{influencer.social_handles.tiktok}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.x && (
                <button
                  onClick={() => handleSocialClick('x', influencer.social_handles.x)}
                  className="social-link"
                  data-testid="x-link"
                >
                  <Twitter />
                  <div>
                    <div className="social-label">X (Twitter)</div>
                    <div className="social-handle">@{influencer.social_handles.x}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.facebook && (
                <button
                  onClick={() => handleSocialClick('facebook', influencer.social_handles.facebook)}
                  className="social-link"
                  data-testid="facebook-link"
                >
                  <Facebook />
                  <div>
                    <div className="social-label">Facebook</div>
                    <div className="social-handle">@{influencer.social_handles.facebook}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.pinterest && (
                <button
                  onClick={() => handleSocialClick('pinterest', influencer.social_handles.pinterest)}
                  className="social-link"
                  data-testid="pinterest-link"
                >
                  <Globe />
                  <div>
                    <div className="social-label">Pinterest</div>
                    <div className="social-handle">@{influencer.social_handles.pinterest}</div>
                  </div>
                </button>
              )}
              
              {influencer.social_handles.podcast && (
                <button
                  onClick={() => handleSocialClick('podcast', influencer.social_handles.podcast)}
                  className="social-link"
                  data-testid="podcast-link"
                >
                  <Podcast />
                  <div>
                    <div className="social-label">Podcast</div>
                    <div className="social-handle">Listen Now</div>
                  </div>
                </button>
              )}
            </div>
          </section>

          <section className="detail-section">
            <h2 className="section-title">Contact Agency</h2>
            <div className="contact-info">
              <Mail className="contact-icon" />
              <a href={`mailto:${influencer.email}`} className="contact-email" data-testid="contact-email">
                {influencer.email}
              </a>
            </div>
            <p className="contact-note">All inquiries are handled through our agency. We'll connect you directly with the influencer.</p>
            <div className="agency-supervision-box">
              <CheckCircle size={20} className="supervision-icon" />
              <p>We ensure and supervise the smooth execution of your service request with optimal results from each selected influencer, artist, or model.</p>
            </div>
          </section>
        </div>
      </div>

      {/* Social Modal */}
      <SocialModal
        open={socialModal.open}
        onClose={() => setSocialModal({ open: false, platform: '', handle: '' })}
        platform={socialModal.platform}
        handle={socialModal.handle}
      />
    </div>
  );
};

export default InfluencerProfile;